import React, { memo, useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Table } from 'reactstrap';

import AppAvatar from 'components/App/Avatar';
import Button from 'components/App/Button';

import { convertIsoDateToString } from 'utils';

import { ReactComponent as ArrowDownIcon } from 'assets/images/monthPicker/arrow-down.svg';

import styles from './DataTableAccommodationAccount.module.scss';

const getRowKey = (item) => {
  if (item.place?._id) {
    return `${item._id}_${item.place._id}`;
  }
  return item._id;
};

const ACCOUNT_TYPE_LABEL = {
  SINGLE: 'Tài khoản Chính',
  SUB: 'Tài khoản phụ',
};

const TABLE_COLUMNS = [
  { key: 'expand', head: '', width: 50 },
  { key: 'user', head: 'Người dùng', minWidth: 280 },
  { key: 'accountType', head: 'Loại tài khoản', minWidth: 140 },
  { key: 'accessRights', head: 'Quyền truy cập', minWidth: 220 },
  { key: 'places', head: 'Quản lý chỗ nghỉ', minWidth: 200 },
  { key: 'lastLogin', head: 'Lần đăng nhập cuối', minWidth: 140 },
  { key: 'actions', head: 'Thao tác', minWidth: 280 },
];

function AccountRow({
  item,
  rowKey,
  isSubAccount,
  isFirstSubAccount,
  isLastSubAccount,
  isParentExpanded,
  isExpanded,
  isLoading,
  onToggleExpand,
  onAddSubAccount,
  onEdit,
  onDelete,
}) {
  const handleAddSubAccount = useCallback(() => {
    onAddSubAccount?.(item._id, item.place?._id, item.place?.name || item.places);
  }, [item._id, item.place?._id, item.place?.name, item.places, onAddSubAccount]);

  const handleEdit = useCallback(() => {
    onEdit?.(item._id, item.place?._id);
  }, [item._id, item.place?._id, onEdit]);

  const handleDelete = useCallback(() => {
    onDelete?.(item._id, item.place?._id);
  }, [item._id, item.place?._id, onDelete]);

  const hasChildren = (item.children || []).length > 0;
  const showExpandBtn = !isSubAccount && (item.place
    ? item.place.managerCount !== 0 && item.place.managerCount !== undefined
    : hasChildren);
  const showAddSubAccount = !isSubAccount && item.accountType !== 'SUB';
  const accountTypeLabel = ACCOUNT_TYPE_LABEL[item.accountType] || item.accountType;

  return (
    <tr
      className={`Table__Body__Row ${
        isSubAccount ? styles.SubRow : ''
      } ${isParentExpanded ? styles.ParentRowExpanded : ''} ${
        isFirstSubAccount ? styles.SubRowFirst : ''
      } ${isLastSubAccount ? styles.SubRowLast : ''}`}
      key={rowKey}
    >
      <td className={`Table__Body__Row__Content ${styles.ExpandCell}`}>
        {showExpandBtn ? (
          <button
            type="button"
            className={styles.ExpandBtn}
            onClick={() => onToggleExpand(rowKey, item)}
            aria-expanded={isExpanded}
            aria-label={isExpanded ? 'Thu gọn' : 'Mở rộng'}
            disabled={isLoading}
          >
            {isLoading ? (
              <i className="bx bx-loader bx-spin" style={{ fontSize: '16px', color: 'var(--bookese-blue-100)' }} />
            ) : (
              <ArrowDownIcon
                className={`${styles.ExpandIcon} ${isExpanded ? styles.ExpandIconOpen : ''}`}
              />
            )}
          </button>
        ) : null}
      </td>

      <td className="Table__Body__Row__Content">
        <div className={`Table__Body__Row__Content__Info ${isSubAccount ? styles.UserInfoIndented : ''}`}>
          <div className="Table__Body__Row__Content__Image">
            <AppAvatar className="avatar-select" name={item.fullName} />
          </div>

          <div>
            <span className="Table__Body__Row__Content__Name">
              {item.fullName}
            </span>

            <div className="Table__Body__Row__Content__Contact">
              <span className="Table__Body__Row__Content__Label">
                {item.email}
              </span>
              <span className="Table__Body__Row__Content__Label">
                {item.phoneNumber}
              </span>
            </div>
          </div>
        </div>
      </td>

      <td className="Table__Body__Row__Content">{accountTypeLabel}</td>

      <td className={`Table__Body__Row__Content ${styles.AccessRightsCell}`}>
        {item.accessRights && (
          <span className={styles.AccessRightsText} title={item.accessRights}>
            {item.accessRights}
          </span>
        )}
      </td>

      <td className={`Table__Body__Row__Content ${styles.PlacesCell}`}>
        {item.places}
      </td>

      <td className="Table__Body__Row__Content">
        <span className="Table__Body__Row__Content__Date">
          {item.lastLogin
            ? convertIsoDateToString(item.lastLogin, 'D [tháng] M')
            : '-'}
        </span>
      </td>

      <td className={`Table__Body__Row__Content ${styles.ActionCell}`}>
        <div className={styles.Action}>
          {showAddSubAccount && (
            <Button
              isDefault
              type="button"
              classes={styles.ActionBtnAddSub}
              onClick={handleAddSubAccount}
            >
              Thêm tài khoản phụ
            </Button>
          )}

          <Button
            isDefault
            type="button"
            classes={styles.ActionBtnEdit}
            onClick={handleEdit}
          >
            Chỉnh sửa
          </Button>

          <Button
            isDefault
            type="button"
            classes={styles.ActionBtnDelete}
            onClick={handleDelete}
          >
            Xóa
          </Button>
        </div>
      </td>
    </tr>
  );
}

AccountRow.propTypes = {
  item: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    fullName: PropTypes.string,
    avatar: PropTypes.string,
    email: PropTypes.string,
    phoneNumber: PropTypes.string,
    accountType: PropTypes.string,
    accessRights: PropTypes.string,
    places: PropTypes.string,
    place: PropTypes.shape({
      _id: PropTypes.string,
      name: PropTypes.string,
      managerCount: PropTypes.number,
    }),
    lastLogin: PropTypes.string,
    children: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
  isSubAccount: PropTypes.bool,
  rowKey: PropTypes.string.isRequired,
  isFirstSubAccount: PropTypes.bool,
  isLastSubAccount: PropTypes.bool,
  isParentExpanded: PropTypes.bool,
  isExpanded: PropTypes.bool,
  isLoading: PropTypes.bool,
  onToggleExpand: PropTypes.func.isRequired,
  onAddSubAccount: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

AccountRow.defaultProps = {
  isSubAccount: false,
  isFirstSubAccount: false,
  isLastSubAccount: false,
  isParentExpanded: false,
  isExpanded: false,
  isLoading: false,
};

function DataTableAccommodationAccount(props) {
  const { data, onAddSubAccount, onEdit, onDelete, onExpand } = props;
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const loadingRowKey = useSelector(
    (state) => state.Administrator.loadingExtranetAdministratorsPlaceManagerKey,
  );

  const onToggleExpand = useCallback((rowKey, item) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(rowKey)) {
        next.delete(rowKey);
      } else {
        next.add(rowKey);
        onExpand?.(item, rowKey);
      }
      return next;
    });
  }, [onExpand]);

  const rows = useMemo(() => {
    const result = [];

    data.forEach((item) => {
      const rowKey = getRowKey(item);
      const isExpanded = expandedIds.has(rowKey);
      const hasChildren = (item.children || []).length > 0;

      result.push({
        rowKey,
        item,
        isSubAccount: false,
        isParentExpanded: hasChildren && isExpanded,
      });

      if (isExpanded) {
        if (loadingRowKey === rowKey) {
          result.push({
            rowKey: `${rowKey}_loading`,
            isLoadingRow: true,
            isSubAccount: true,
            isFirstSubAccount: true,
            isLastSubAccount: true,
          });
        } else if (hasChildren) {
          const children = item.children || [];
          children.forEach((child, index) => {
            const childItem = {
              ...child,
              place: child.place || item.place,
            };
            result.push({
              rowKey: `sub_${rowKey}_${getRowKey(childItem)}`,
              item: childItem,
              isSubAccount: true,
              isFirstSubAccount: index === 0,
              isLastSubAccount: index === children.length - 1,
            });
          });
        }
      }
    });

    return result;
  }, [data, expandedIds, loadingRowKey]);

  if (data.length === 0) {
    return <div className="NoData">Không có dữ liệu</div>;
  }

  return (
    <div className={styles.Component}>
      <Table responsive className="Table">
        <thead className="Table__Header">
          <tr className="Table__Header__Row">
            {TABLE_COLUMNS.map((column) => (
              <th
                key={column.key}
                className={`Table__Header__Row__Content ${
                  column.key === 'expand' ? styles.ExpandCell : ''
                } ${column.key === 'actions' ? styles.ActionCell : ''}`}
                style={
                  column.width
                    ? { width: `${column.width}px`, minWidth: `${column.width}px` }
                    : { minWidth: `${column.minWidth}px` }
                }
              >
                {column.head}
              </th>
            ))}
          </tr>
        </thead>

        <tbody className="Table__Body">
          {rows.map(({
            rowKey,
            item,
            isSubAccount,
            isFirstSubAccount,
            isLastSubAccount,
            isParentExpanded,
            isLoadingRow,
          }) => {
            if (isLoadingRow) {
              return (
                <tr
                  className={`Table__Body__Row ${styles.SubRow} ${styles.SubRowFirst} ${styles.SubRowLast}`}
                  key={rowKey}
                >
                  <td colSpan={7} className="text-center py-3">
                    <div
                      className="d-flex align-items-center justify-content-center"
                      style={{ gap: '8px' }}
                    >
                      <i
                        className="bx bx-loader bx-spin"
                        style={{
                          fontSize: '18px',
                          color: 'var(--bookese-blue-100)',
                        }}
                      />
                      <span
                        style={{
                          color: 'var(--bookese-gray-500)',
                          fontSize: '13px',
                        }}
                      >
                        Đang tải tài khoản phụ...
                      </span>
                    </div>
                  </td>
                </tr>
              );
            }

            return (
              <AccountRow
                key={rowKey}
                rowKey={rowKey}
                item={item}
                isSubAccount={isSubAccount}
                isFirstSubAccount={isFirstSubAccount}
                isLastSubAccount={isLastSubAccount}
                isParentExpanded={isParentExpanded}
                isExpanded={expandedIds.has(rowKey)}
                isLoading={loadingRowKey === rowKey}
                onToggleExpand={onToggleExpand}
                onAddSubAccount={onAddSubAccount}
                onEdit={onEdit}
                onDelete={onDelete}
              />
            );
          })}
        </tbody>
      </Table>
    </div>
  );
}

DataTableAccommodationAccount.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({
    _id: PropTypes.string.isRequired,
    fullName: PropTypes.string,
    avatar: PropTypes.string,
    email: PropTypes.string,
    phoneNumber: PropTypes.string,
    accountType: PropTypes.string,
    accessRights: PropTypes.string,
    places: PropTypes.string,
    place: PropTypes.shape({
      _id: PropTypes.string,
      name: PropTypes.string,
      managerCount: PropTypes.number,
    }),
    lastLogin: PropTypes.string,
    children: PropTypes.arrayOf(PropTypes.shape({})),
  })),
  onAddSubAccount: PropTypes.func,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  onExpand: PropTypes.func,
};

DataTableAccommodationAccount.defaultProps = {
  data: [],
  onAddSubAccount: undefined,
  onEdit: undefined,
  onDelete: undefined,
  onExpand: undefined,
};

export default memo(DataTableAccommodationAccount);
